import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Grid, Snackbar, Alert, Dialog, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BuildingDetailsCeilingsAddCeiling from './BuildingDetailsCeilingsAddCeiling';

const BuildingDetailsCeilings = () => {
    const { id } = useParams();
    const [ceilings, setCeilings] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCeiling, setEditingCeiling] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    const fetchCeilings = () => {
        axios.get(`/buildings/${id}/ceiling/`)
            .then(res => setCeilings(res.data))
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to fetch ceilings.', severity: 'error' });
            });
    };

    useEffect(() => {
        fetchCeilings();
    }, [id]);

    const paginatedCeilings = ceilings.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    const totalPages = Math.ceil(ceilings.length / itemsPerPage);

    return (
        <Box sx={{ my: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Ceilings
                </Typography>
                {ceilings.length === 0 && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setModalOpen(true)}
                    >
                        Add Ceiling
                    </Button>
                )}
            </Box>

            {paginatedCeilings.map((ceiling) => (
                <Accordion
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: 'none',
                        border: '1px solid #e0e0e0',
                        '&::before': { display: 'none' }
                    }}
                    key={ceiling.id}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                            Ceiling #{ceiling.id}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            sx={{ mr: 1 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                axios.get(`/buildings/${id}/ceiling/${ceiling.id}/`)
                                    .then(res => {
                                        setEditingCeiling(res.data);
                                        setModalOpen(true);
                                    })
                                    .catch(() => {
                                        setSnackbar({ open: true, message: 'Failed to load ceiling for editing.', severity: 'error' });
                                    });
                            }}
                        >
                            Edit
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            sx={{ mr: 2 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                axios.delete(`/buildings/${id}/ceiling/${ceiling.id}/`)
                                    .then(() => {
                                        setSnackbar({ open: true, message: 'Ceiling deleted successfully.', severity: 'success' });
                                        fetchCeilings();
                                    })
                                    .catch(() => {
                                        setSnackbar({ open: true, message: 'Failed to delete ceiling.', severity: 'error' });
                                    });
                            }}
                        >
                            Delete
                        </Button>
                    </AccordionSummary>

                    <AccordionDetails sx={{ backgroundColor: '#f5f9f6' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Area (m²)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {ceiling.area}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">U-Value (Insulated)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {ceiling.u_value}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">U-Value (Uninsulated)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    {ceiling.u_value_uninsulated}
                                </Typography>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                />
            </Box>

            <Dialog open={modalOpen} onClose={() => { setModalOpen(false); setEditingCeiling(null); }}>
                <BuildingDetailsCeilingsAddCeiling
                    buildingId={id}
                    ceiling={editingCeiling}
                    onSuccess={() => {
                        fetchCeilings();
                        setSnackbar({
                            open: true,
                            message: editingCeiling ? 'Ceiling updated successfully!' : 'Ceiling added successfully!',
                            severity: 'success'
                        });
                        setEditingCeiling(null);
                        setModalOpen(false);
                    }}
                    onError={() => {
                        setSnackbar({ open: true, message: 'Failed to save ceiling.', severity: 'error' });
                    }}
                    onClose={() => { setModalOpen(false); setEditingCeiling(null); }}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BuildingDetailsCeilings;
